import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, X, MapPin } from "lucide-react";
import { adminListCities, adminCreateCity, adminUpdateCity, adminDeleteCity } from "@/lib/api";
import { useSite } from "@/lib/site";

const emptyCity = { name: "", slug: "", region: "Uttarakhand", intro: "", why: "", order: 0, published: true };
const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").slice(0, 60);
const errMsg = (err) => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : d?.[0]?.msg || "Save failed";
};

export default function CitiesPanel() {
  const { reloadCities } = useSite();
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => adminListCities().then(setCities).catch(() => toast.error("Failed to load cities"));
  useEffect(() => { load(); }, []);
  const refresh = () => { load(); reloadCities(); };

  const openNew = () => { setEditing(null); setForm(emptyCity); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c, why: (c.why || []).join("\n") }); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, order: Number(form.order) || 0, why: form.why.split("\n").map((w) => w.trim()).filter(Boolean) };
    delete payload.id; delete payload.created_at;
    try {
      if (editing) await adminUpdateCity(editing.id, payload); else await adminCreateCity(payload);
      toast.success(editing ? "City updated" : "City added");
      setForm(null); refresh();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const toggle = async (c) => {
    try { await adminUpdateCity(c.id, { published: !c.published }); refresh(); }
    catch { toast.error("Update failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}? All 8 service pages for this city will go offline.`)) return;
    try { await adminDeleteCity(c.id); toast.success("Deleted"); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="cities-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="text-sm text-[color:var(--dh-slate)]">{cities.length} cities · each one adds 8 local service pages</div>
        <button onClick={openNew} className="btn-primary" data-testid="city-add"><Plus className="w-4 h-4" /> New City</button>
      </div>
      <div className="space-y-3">
        {cities.map((c) => (
          <CityRow key={c.id} c={c} onEdit={() => openEdit(c)} onToggle={() => toggle(c)} onDelete={() => remove(c)} />
        ))}
        {!cities.length && <div className="p-12 text-center border border-[color:var(--dh-line)] rounded-2xl text-[color:var(--dh-slate)]">No cities yet.</div>}
      </div>
      {form && <CityForm form={form} setForm={setForm} editing={editing} onClose={() => setForm(null)} onSave={save} />}
    </div>
  );
}

function CityRow({ c, onEdit, onToggle, onDelete }) {
  const btn = "p-2 rounded-lg border border-[color:var(--dh-line)] hover:border-[color:var(--dh-orange)]";
  return (
    <div className="p-4 rounded-2xl border border-[color:var(--dh-line)] bg-white flex gap-4 items-center" data-testid={`city-item-${c.slug}`}>
      <div className="w-11 h-11 rounded-xl bg-[color:var(--dh-orange)]/15 text-[color:var(--dh-orange)] grid place-items-center shrink-0"><MapPin className="w-5 h-5" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display font-bold text-[color:var(--dh-navy)]">{c.name}</div>
          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${c.published ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{c.published ? "Live" : "Hidden"}</span>
        </div>
        <div className="text-xs text-[color:var(--dh-slate)] mt-1 truncate">/services/…/{c.slug} · {c.region} · order {c.order}</div>
      </div>
      <div className="flex gap-1 shrink-0">
        <button onClick={onToggle} className={btn} data-testid={`city-toggle-${c.slug}`} title={c.published ? "Hide" : "Publish"}>{c.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
        <button onClick={onEdit} className={btn} data-testid={`city-edit-${c.slug}`} title="Edit"><Pencil className="w-4 h-4" /></button>
        <button onClick={onDelete} className="p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50" data-testid={`city-delete-${c.slug}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function CityForm({ form, setForm, editing, onClose, onSave }) {
  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v, ...(k === "name" && !editing ? { slug: slugify(v) } : {}) }));
  };
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 grid place-items-center p-4 overflow-y-auto" onClick={onClose} data-testid="city-form-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSave} className="bg-white rounded-3xl p-6 w-full max-w-xl border border-[color:var(--dh-line)] my-8 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-display text-xl text-[color:var(--dh-navy)]">{editing ? `Edit ${editing.name}` : "New City"}</div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="city-form-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <L label="City name *"><input required value={form.name} onChange={set("name")} className="input-admin" data-testid="city-name" placeholder="e.g. Nainital" /></L>
          <L label="Slug *"><input required value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} className="input-admin" data-testid="city-slug" /></L>
          <L label="Region / State"><input value={form.region} onChange={set("region")} className="input-admin" data-testid="city-region" /></L>
          <L label="Order"><input type="number" value={form.order} onChange={set("order")} className="input-admin" data-testid="city-order" /></L>
        </div>
        <L label="Local intro * (shown in hero)"><textarea required minLength={10} rows={4} value={form.intro} onChange={set("intro")} className="input-admin resize-none" data-testid="city-intro-input" /></L>
        <L label="Why choose us here (one point per line, max 8)"><textarea rows={4} value={form.why} onChange={set("why")} className="input-admin resize-none" data-testid="city-why-input" /></L>
        <label className="flex items-center gap-2 text-sm text-[color:var(--dh-slate)]">
          <input type="checkbox" checked={form.published} onChange={set("published")} className="w-4 h-4 accent-[color:var(--dh-orange)]" data-testid="city-published" /> Live on site
        </label>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost-dark flex-1 justify-center">Cancel</button>
          <button type="submit" className="btn-primary flex-1 justify-center" data-testid="city-save">{editing ? "Save changes" : "Add city"}</button>
        </div>
        <style>{`.input-admin { width:100%; padding:.7rem 1rem; border-radius:12px; border:1px solid var(--dh-line); background:#fff; font-size:14px; color:var(--dh-ink); transition:border-color .2s ease; } .input-admin:focus { outline:none; border-color: var(--dh-orange); box-shadow: 0 0 0 3px rgba(255,107,26,0.15); }`}</style>
      </form>
    </div>
  );
}

function L({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--dh-navy)] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
